/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {Helmet} from "react-helmet-async";
import { Check, Star, Crown, Building2, Users, Zap } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePaymentContext } from "../context/PaymentContext";
import { useDarkMode } from "../context/DarkModeContext";
import { packageService } from "../utils/PackageService";
import type { Package } from "../types/package";
import type { Plan } from "../types/stripe";

interface WhitelistPlansProps {
  onSubscribe?: (pkg: Package, billingPeriod: "monthly" | "annual") => void;
  onMpesaPayment?: (
    pkg: Package,
    isAnnual: boolean,
    phoneNumber: string,
  ) => void;
}

// Extended interface for API response
interface ApiResponse {
  packages?: Package[];
  data?: Package[];
}

const WhitelistPlans: React.FC<WhitelistPlansProps> = () => {
  useAuth();
  const {
    state: paymentState,
    setPlan,
    setBillingPeriod,
  } = usePaymentContext();
  const { isDark } = useDarkMode();
  const navigate = useNavigate();

  const billingPeriod = paymentState.billingPeriod;

  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch whitelist packages only
  useEffect(() => {
    const fetchWhitelistPackages = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log("🔄 Fetching whitelist packages...");

        // Use the specific method for whitelist packages
        const responseData = (await packageService.getWhitelistPackages()) as
          | Package[]
          | ApiResponse;

        console.log("📦 Whitelist API Response:", responseData);

        // Handle different possible response structures
        let packagesData: Package[];

        if (Array.isArray(responseData)) {
          packagesData = responseData;
        } else if (
          responseData &&
          Array.isArray((responseData as ApiResponse).packages)
        ) {
          packagesData = (responseData as ApiResponse).packages!;
        } else if (
          responseData &&
          Array.isArray((responseData as ApiResponse).data)
        ) {
          packagesData = (responseData as ApiResponse).data!;
        } else {
          console.warn("Unexpected API response structure:", responseData);
          packagesData = [];
        }

        // Filter only active whitelist packages and sort by orderIndex
        const activeWhitelistPackages = packagesData
          .filter((pkg) => pkg.isActive && pkg.type === "whitelist_request")
          .sort((a, b) => a.orderIndex - b.orderIndex);

        console.log(
          "✅ Active whitelist packages:",
          activeWhitelistPackages.length,
        );
        console.log(
          "📊 Packages details:",
          activeWhitelistPackages.map((p) => ({
            name: p.name,
            type: p.type,
            isActive: p.isActive,
            monthlyPrice: p.monthlyPrice,
          })),
        );

        setPackages(activeWhitelistPackages);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to load whitelist packages";
        setError(errorMessage);
        console.error("❌ Error fetching whitelist packages:", err);
        setPackages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWhitelistPackages();
  }, []);

  // Convert Package to Plan for compatibility with PaymentContext
  const convertPackageToPlan = (pkg: Package): Plan => {
    // Use maxJobPostings as the primary source, fallback to maxJobPosts or maxJobs
    const maxJobs = pkg.maxJobPostings || pkg.maxJobPosts || pkg.maxJobs;

    return {
      id: pkg.id,
      name: pkg.name,
      description: pkg.description || "",
      price: billingPeriod === "annual" ? pkg.annualPrice : pkg.monthlyPrice,
      monthlyPrice: pkg.monthlyPrice,
      annualPrice: pkg.annualPrice,
      maxJobs,
      durationDays:
        billingPeriod === "annual" ? pkg.annualDuration : pkg.monthlyDuration,
      features: pkg.features.map((f) => f.text),
      isPopular: pkg.isPopular,
      // Whitelist specific fields - use optional chaining and provide defaults
      whitelistRequests: pkg.whitelistRequests || 0,
      isWhitelistAutoApprove: pkg.isWhitelistAutoApprove || false,
    };
  };

  const handleGetStarted = (pkg: Package) => {
    // Convert Package to Plan and set in context
    const plan = convertPackageToPlan(pkg);
    setPlan(plan, billingPeriod);

    const price =
      billingPeriod === "annual" ? pkg.annualPrice : pkg.monthlyPrice;
    const isAnnual = billingPeriod === "annual";

    // Navigate to payment-forms with state
    navigate("/payment-forms", {
      state: {
        package: pkg,
        price,
        isAnnual,
        packageType: "whitelist_request",
        isWhitelist: true,
      },
    });
  };

  // Update billing period handler
  const handleBillingPeriodChange = (period: "monthly" | "annual") => {
    setBillingPeriod(period);

    // If we have a selected plan, update it with the new billing period
    if (paymentState.selectedPlan) {
      setPlan(paymentState.selectedPlan, period);
    }
  };

  // Calculate savings percentage
  const calculateSavings = (
    originalPrice: number,
    currentPrice: number,
  ): number => {
    if (!originalPrice || originalPrice <= currentPrice) return 0;
    return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
  };

  // Format price for display
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Get duration display text
  const getDurationText = (duration: number) => {
    if (duration === 7) return "7 Days";
    if (duration === 30) return "1 Month";
    if (duration === 90) return "3 Months";
    if (duration === 365) return "1 Year";
    return `${duration} Days`;
  };

  // Calculate total savings for annual billing toggle
  const calculateTotalSavings = () => {
    if (billingPeriod !== "annual") return 0;

    let maxSavings = 0;
    packages.forEach((pkg) => {
      const originalPrice = pkg.originalAnnualPrice;
      const currentPrice = pkg.annualPrice;
      if (originalPrice && currentPrice) {
        const savings = calculateSavings(originalPrice, currentPrice);
        if (savings > maxSavings) maxSavings = savings;
      }
    });
    return maxSavings;
  };

  // Check if a plan is currently selected in context
  const isPackageSelected = (pkgId: string) => {
    return paymentState.selectedPlan?.id === pkgId;
  };

  // Get feature icon based on feature text
  const getFeatureIcon = (featureText: string) => {
    if (
      featureText.toLowerCase().includes("job") ||
      featureText.toLowerCase().includes("post")
    ) {
      return <Building2 size={16} className="text-blue-500 mr-2" />;
    } else if (
      featureText.toLowerCase().includes("whitelist") ||
      featureText.toLowerCase().includes("request")
    ) {
      return <Users size={16} className="text-green-500 mr-2" />;
    } else if (
      featureText.toLowerCase().includes("premium") ||
      featureText.toLowerCase().includes("priority")
    ) {
      return <Crown size={16} className="text-yellow-500 mr-2" />;
    } else if (
      featureText.toLowerCase().includes("auto") ||
      featureText.toLowerCase().includes("instant")
    ) {
      return <Zap size={16} className="text-orange-500 mr-2" />;
    }
    return <Check size={16} className="text-green-500 mr-2" />;
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen py-8 ${
          isDark
            ? "bg-gradient-to-br from-gray-900 to-gray-800"
            : "bg-gradient-to-br from-blue-50 to-indigo-50"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="animate-pulse">
              <div
                className={`h-12 rounded w-2/3 sm:w-1/3 mx-auto mb-4 ${
                  isDark ? "bg-gray-700" : "bg-blue-200"
                }`}
              ></div>
              <div
                className={`h-6 rounded w-3/4 sm:w-1/2 mx-auto mb-8 ${
                  isDark ? "bg-gray-700" : "bg-blue-200"
                }`}
              ></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`rounded-2xl p-6 sm:p-8 h-96 animate-pulse border ${
                  isDark
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-blue-50"
                }`}
              >
                <div
                  className={`h-8 rounded mb-4 ${
                    isDark ? "bg-gray-700" : "bg-blue-100"
                  }`}
                ></div>
                <div
                  className={`h-12 rounded mb-4 ${
                    isDark ? "bg-gray-700" : "bg-blue-100"
                  }`}
                ></div>
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((j) => (
                    <div
                      key={j}
                      className={`h-4 rounded ${
                        isDark ? "bg-gray-700" : "bg-blue-100"
                      }`}
                    ></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`min-h-screen py-8 ${
          isDark
            ? "bg-gradient-to-br from-gray-900 to-gray-800"
            : "bg-gradient-to-br from-blue-50 to-indigo-50"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className={`rounded-2xl p-6 sm:p-8 max-w-2xl mx-auto border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-white border-blue-50"
            }`}
          >
            <h2
              className={`text-2xl font-bold mb-4 ${
                isDark ? "text-red-400" : "text-red-800"
              }`}
            >
              Error Loading Whitelist Plans
            </h2>
            <p className={`mb-6 ${isDark ? "text-red-300" : "text-red-600"}`}>
              {error}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-colors font-semibold shadow-md"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div
        className={`min-h-screen py-8 ${
          isDark
            ? "bg-gradient-to-br from-gray-900 to-gray-800"
            : "bg-gradient-to-br from-blue-50 to-indigo-50"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className={`rounded-2xl shadow-lg border p-8 sm:p-12 max-w-2xl mx-auto ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-white border-blue-50"
            }`}
          >
            <Building2
              size={64}
              className={`mx-auto mb-4 ${
                isDark ? "text-blue-400" : "text-blue-500"
              }`}
            />
            <h2
              className={`text-2xl font-bold mb-4 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              No Whitelist Plans Available
            </h2>
            <p className={`mb-6 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              No whitelist packages are currently available. Please check back
              later or contact our sales team for enterprise solutions.
            </p>
            <button
              onClick={() => navigate("/contact")}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-colors font-semibold shadow-md"
            >
              Contact Sales
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen py-8 ${
        isDark
          ? 'bg-gradient-to-br from-gray-900 to-gray-800'
          : 'bg-gradient-to-br from-blue-50 to-indigo-50'
      }`}
    >
      <Helmet>
        <title>
          Hire Workers in Kenya - Employer & Enterprise Plans | FindWork
        </title>
        <meta
          name="description"
          content="Post jobs and find ready-to-work people near you. Enterprise whitelist packages for businesses and organizations across Kenya."
        />
        <link rel="canonical" href="https://findwork.ltd/employers-packages" />
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex justify-center mb-4">
            <div
              className={`p-3 rounded-full ${isDark ? 'bg-blue-900' : 'bg-blue-100'}`}
            >
              <Building2
                size={32}
                className={isDark ? 'text-blue-400' : 'text-blue-600'}
              />
            </div>
          </div>
          <h1
            className={`text-3xl sm:text-4xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            Enterprise Whitelist Packages
          </h1>
          <p
            className={`text-lg sm:text-xl mb-6 sm:mb-8 px-4 ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}
          >
            Premium packages for businesses and organizations requiring
            whitelist access
          </p>

          {/* Billing Toggle */}
          <div className="flex justify-center sm:justify-end mb-6 sm:mb-8">
            <div
              className={`rounded-full p-1.5 shadow-sm border ${
                isDark
                  ? 'bg-gray-800 border-gray-700'
                  : 'bg-white border-blue-100'
              }`}
            >
              <div className="flex items-center space-x-2 sm:space-x-4">
                <span
                  className={`font-medium text-sm sm:text-base px-2 sm:px-0 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Billing:
                </span>
                <div
                  className={`flex rounded-full p-1 ${
                    isDark ? 'bg-gray-700' : 'bg-blue-50'
                  }`}
                >
                  <button
                    onClick={() => handleBillingPeriodChange('monthly')}
                    className={`px-4 sm:px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                      billingPeriod === 'monthly'
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg transform scale-105'
                        : isDark
                          ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-600'
                          : 'text-gray-600 hover:text-blue-600 hover:bg-white'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => handleBillingPeriodChange('annual')}
                    className={`px-4 sm:px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                      billingPeriod === 'annual'
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg transform scale-105'
                        : isDark
                          ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-600'
                          : 'text-gray-600 hover:text-blue-600 hover:bg-white'
                    }`}
                  >
                    Annual
                    {calculateTotalSavings() > 0 && (
                      <span className="ml-1 sm:ml-2 bg-green-500 text-white px-1.5 sm:px-2 py-0.5 rounded-full text-xs">
                        Save {calculateTotalSavings()}%
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Whitelist Packages Grid */}
        <div
          className={`grid ${
            packages.length === 1
              ? 'grid-cols-1 max-w-2xl mx-auto'
              : packages.length === 2
                ? 'grid-cols-1 md:grid-cols-2'
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          } gap-6 sm:gap-8 mb-8 sm:mb-12`}
        >
          {packages.map((pkg) => {
            const price =
              billingPeriod === 'annual' ? pkg.annualPrice : pkg.monthlyPrice;
            const originalPrice =
              billingPeriod === 'annual'
                ? pkg.originalAnnualPrice
                : pkg.originalMonthlyPrice;
            const duration =
              billingPeriod === 'annual'
                ? pkg.annualDuration
                : pkg.monthlyDuration;
            const savings = originalPrice
              ? calculateSavings(originalPrice, price)
              : 0;
            const isPopular = pkg.isPopular;
            const isSelected = isPackageSelected(pkg.id);

            // Use maxJobPostings as primary, fallback to maxJobPosts or maxJobs
            const maxJobPosts =
              pkg.maxJobPostings || pkg.maxJobPosts || pkg.maxJobs;

            return (
              <div
                key={pkg.id}
                className={`relative rounded-2xl shadow-lg border-2 transition-all duration-300 hover:scale-105 hover:shadow-xl ${
                  isSelected
                    ? 'border-indigo-500 ring-4 ring-indigo-100 transform scale-105'
                    : isPopular
                      ? 'border-blue-500 transform scale-105'
                      : isDark
                        ? 'border-gray-700'
                        : 'border-blue-100'
                } ${isDark ? 'bg-gray-800' : 'bg-white'}`}
              >
                {isSelected && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <div className="bg-indigo-500 text-white px-4 sm:px-6 py-1 rounded-full text-xs sm:text-sm font-semibold flex items-center whitespace-nowrap">
                      <Check size={14} className="mr-1" />
                      Selected
                    </div>
                  </div>
                )}

                {isPopular && !isSelected && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 sm:px-6 py-1 rounded-full text-xs sm:text-sm font-semibold flex items-center whitespace-nowrap">
                      <Star size={14} className="mr-1" />
                      Recommended
                    </div>
                  </div>
                )}

                {savings > 0 && (
                  <div className="absolute -top-3 sm:-top-4 right-4 z-10">
                    <div className="bg-green-500 text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-semibold">
                      Save {savings}%
                    </div>
                  </div>
                )}

                <div className="p-6 sm:p-8">
                  {/* Package Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3
                        className={`text-xl sm:text-2xl font-bold ${
                          isDark ? 'text-white' : 'text-gray-900'
                        }`}
                      >
                        {pkg.name}
                      </h3>
                      {pkg.description && (
                        <p
                          className={`mt-1 text-sm ${
                            isDark ? 'text-gray-300' : 'text-gray-600'
                          }`}
                        >
                          {pkg.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="mb-4">
                    <span
                      className={`text-3xl sm:text-4xl font-bold ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}
                    >
                      {formatPrice(price)}
                    </span>
                    <span
                      className={`ml-2 text-sm sm:text-base ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      /{billingPeriod === 'monthly' ? 'month' : 'year'}
                    </span>
                    {originalPrice && originalPrice > price && (
                      <div
                        className={`text-sm mt-1 ${
                          isDark
                            ? 'text-gray-500 line-through'
                            : 'text-gray-500 line-through'
                        }`}
                      >
                        {formatPrice(originalPrice)}
                      </div>
                    )}
                  </div>

                  {/* Whitelist-specific details */}
                  <div className="mb-4 space-y-2">
                    {maxJobPosts > 0 && (
                      <div
                        className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-600'}`}
                      >
                        📊 {maxJobPosts === null ? 'Unlimited' : maxJobPosts}{' '}
                        Job Posts
                      </div>
                    )}
                    {pkg.whitelistRequests && pkg.whitelistRequests > 0 && (
                      <div
                        className={`text-sm ${isDark ? 'text-green-300' : 'text-green-600'}`}
                      >
                        👥 {pkg.whitelistRequests} Whitelist Requests
                      </div>
                    )}
                    {pkg.isWhitelistAutoApprove && (
                      <div
                        className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-600'}`}
                      >
                        ⚡ Auto-Approval Enabled
                      </div>
                    )}
                    <div
                      className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                    >
                      ⏱️ {getDurationText(duration)}
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                    {pkg.features
                      .sort((a, b) => a.orderIndex - b.orderIndex)
                      .map((feature) => (
                        <li key={feature.id} className="flex items-start">
                          {getFeatureIcon(feature.text)}
                          <span
                            className={`text-sm sm:text-base ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}
                          >
                            {feature.text}
                          </span>
                        </li>
                      ))}
                  </ul>

                  {/* Action Button */}
                  <button
                    onClick={() => handleGetStarted(pkg)}
                    className={`w-full py-3 px-4 sm:px-6 rounded-lg font-semibold transition-all flex items-center justify-center ${
                      isSelected
                        ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-lg'
                        : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md'
                    }`}
                  >
                    {isSelected ? 'Proceed to Payment' : `Get ${pkg.name}`}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Information */}
        <div
          className={`rounded-2xl shadow-lg border p-6 sm:p-8 text-center ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-blue-50'
          }`}
        >
          <Crown
            size={40}
            className={`mx-auto mb-4 ${
              isDark ? 'text-blue-400' : 'text-blue-500'
            }`}
          />
          <h3
            className={`text-xl sm:text-2xl font-bold mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            Enterprise Benefits
          </h3>
          <p
            className={`mb-6 text-sm sm:text-base max-w-2xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}
          >
            Whitelist packages include priority support, enhanced job posting
            capabilities, and enterprise-grade features designed for growing
            businesses and organizations.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div
              className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}
            >
              <Building2
                size={24}
                className={`mx-auto mb-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}
              />
              <div className={isDark ? 'text-blue-300' : 'text-blue-700'}>
                Enhanced Job Posting
              </div>
            </div>
            <div
              className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}
            >
              <Users
                size={24}
                className={`mx-auto mb-2 ${isDark ? 'text-green-400' : 'text-green-600'}`}
              />
              <div className={isDark ? 'text-green-300' : 'text-green-700'}>
                Whitelist Access
              </div>
            </div>
            <div
              className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}
            >
              <Zap
                size={24}
                className={`mx-auto mb-2 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}
              />
              <div className={isDark ? 'text-yellow-300' : 'text-yellow-700'}>
                Priority Support
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhitelistPlans;
